<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { CloudApiError, enterpriseProfileApi } from '@/api/index.js'
import { authStore } from '@/stores/auth.js'
import { projectScope, publishProjectChange } from '@/utils/projectRefresh.js'
import { createUrlFeedback, organizedProfile } from '@/utils/profileUrlFeedback.js'
import PastedTextField from '@/components/PastedTextField.vue'

const router = useRouter()
const tenantId = computed(() => authStore.state.selectedTenant?.id || '')
const project = computed(() => authStore.state.assignment?.project || null)
const context = { tenantId: tenantId.value, projectId: project.value?.id }
const profileFields = Object.freeze([
  { key: 'companyName', label: '企业名称', placeholder: '例如：某某连锁加盟品牌', required: true },
  { key: 'industry', label: '所属行业', placeholder: '例如：商用净水 / 健康饮水服务', required: true },
  { key: 'productService', label: '产品/服务', placeholder: '企业主要卖什么、服务什么客户', required: true, wide: true },
  { key: 'painPoints', label: '产品/服务解决痛点', placeholder: '客户现在最难、最急、最怕的问题', required: true, wide: true, multiline: true },
  { key: 'highlights', label: '产品服务三大亮点', placeholder: '一行一个亮点，填写真实可证明的优势', required: true, wide: true, multiline: true },
  { key: 'serviceRegions', label: '服务地区', placeholder: '例如：上海、华东、全国', required: true },
  { key: 'targetUsers', label: '现阶段目标用户', placeholder: '企业当前最希望服务和转化的客户', required: true, wide: true, multiline: true },
  { key: 'acquisitionMethods', label: '现阶段获客方式', placeholder: '例如：老客户介绍、公开内容、线下服务网络', required: true, wide: true, multiline: true },
  { key: 'competitors', label: '主要竞争对手', placeholder: '一行一个真实竞品品牌、企业或账号名称', wide: true, multiline: true },
  { key: 'competitorProducts', label: '竞品产品/服务', placeholder: '竞品具体提供什么，客户为什么会拿来比较', wide: true, multiline: true },
  { key: 'competitorChannels', label: '竞品平台/账号', placeholder: '小红书、抖音、TikTok 等公开平台账号', wide: true, multiline: true },
  { key: 'competitorUrls', label: '竞品官网/原文网址', placeholder: '至少填写一个公开网址，例如：竞品名称 | https://example.com', required: true, wide: true, multiline: true },
])
const form = reactive(Object.fromEntries(profileFields.map((field) => [field.key, ''])))
const textFields = {}, textStates = reactive({}), pastedSources = ref({})
const workspace = ref(null)
const ready = ref(false)
const saving = ref(false)
const publishing = ref(false)
const saveState = ref('正在读取云端资料…')
const errorMessage = ref('')
const conflictDraft = ref(null)
const touched = reactive({})
const urlFeedback = ref({ status: 'idle', result: null })
const urlChecker = createUrlFeedback(
  (value, signal) => enterpriseProfileApi.validateUrls(context.tenantId, context.projectId, value, { signal, timeoutMs: 10000 }),
  state => { urlFeedback.value = state },
)
const organizePreview = ref(null)
const organizedUndo = ref(null)
const urlIssues = computed(() => urlFeedback.value.result?.issues || [])
function fieldError(field) {
  if (field.key === 'competitorUrls') return ['invalid', 'error'].includes(urlFeedback.value.status) ? urlFeedback.value.message : ''
  return touched[field.key] && field.required && !form[field.key].trim() ? `请填写${field.label}。` : ''
}
function previewUrls() { organizePreview.value = organizedProfile(form, urlFeedback.value.result) }
function applyOrganizedUrls() {
  const preview = organizePreview.value
  if (!preview || preview.before.competitorUrls !== form.competitorUrls || preview.before.competitorProducts !== form.competitorProducts) return
  Object.assign(form, preview.after)
  organizedUndo.value = preview
  organizePreview.value = null
}
function undoOrganizedUrls() {
  const change = organizedUndo.value
  if (!change || Object.keys(change.after).some(key => form[key] !== change.after[key])) return
  Object.assign(form, change.before)
  organizedUndo.value = null
}
const canUndoUrls = computed(() => organizedUndo.value && Object.keys(organizedUndo.value.after).every(key => form[key] === organizedUndo.value.after[key]))
const hasUnpublishedChanges = computed(() => Boolean(
  workspace.value?.draft_hash
  && workspace.value?.draft_hash !== workspace.value?.current?.profile_hash,
))
let saveTimer = null
let retryTimer = null
let rerunSave = false
let lastSavedJson = ''
const formalSaveConfirmed = computed(() => Boolean(workspace.value?.current && !errorMessage.value
  && !hasUnpublishedChanges.value && JSON.stringify(normalizedDraft()) === lastSavedJson))

function savedLabel() {
  if (workspace.value?.current && !hasUnpublishedChanges.value) {
    return `正式版本 v${workspace.value.current.revision} 已保存`
  }
  return workspace.value?.draft_revision ? '草稿已保存到云端' : '尚未保存草稿'
}

function normalizedDraft() {
  const draft = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, String(value || '')]))
  const sources = Object.fromEntries(Object.entries(pastedSources.value).filter(([key, source]) => !source.bound_text || source.bound_text === draft[key]))
  if (Object.keys(sources).length) draft._pasted_sources = sources
  return draft
}

function updateSource(key, source) {
  const next = { ...pastedSources.value }
  if (source) next[key] = source
  else delete next[key]
  pastedSources.value = next
}

function stableHash(value) {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

function applyRemote(remote) {
  workspace.value = remote
  const draft = Number(remote?.draft_revision || 0) > 0
    ? (remote?.draft || {})
    : (remote?.current?.profile || {})
  for (const key of Object.keys(form)) form[key] = String(draft[key] || '')
  pastedSources.value = draft._pasted_sources || {}
  lastSavedJson = JSON.stringify(normalizedDraft())
}

async function loadProfile() {
  ready.value = false
  errorMessage.value = ''
  let lastError = null
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      const remote = await enterpriseProfileApi.get(context.tenantId, context.projectId, { timeoutMs: 20_000 })
      applyRemote(remote)
      ready.value = true
      saveState.value = savedLabel()
      return
    } catch (error) {
      lastError = error
      saveState.value = attempt < 3 ? `云端读取中断，正在自动重试（${attempt}/2）…` : '读取失败'
      if (attempt < 3) await new Promise((resolve) => setTimeout(resolve, attempt * 1000))
    }
  }
  throw lastError
}

function scheduleRetry() {
  clearTimeout(retryTimer)
  retryTimer = setTimeout(() => saveDraft(), 3000)
}

async function saveDraft(force = false) {
  clearTimeout(saveTimer)
  clearTimeout(retryTimer)
  if (!ready.value || !workspace.value) return false
  if (Object.values(textStates).some(state => state.checking)) { scheduleRetry(); return false }
  const draft = normalizedDraft()
  const snapshot = JSON.stringify(draft)
  if (new TextEncoder().encode(snapshot).length > 256 * 1024) {
    saveState.value = '资料过长，尚未保存'
    errorMessage.value = '企业资料超过 256 KiB，完整输入已保留，请缩短后再保存。'
    return false
  }
  if (!force && snapshot === lastSavedJson) {
    saveState.value = savedLabel()
    return true
  }
  if (saving.value) {
    rerunSave = true
    return false
  }
  saving.value = true
  saveState.value = '正在保存到云端…'
  errorMessage.value = ''
  const expectedRevision = workspace.value.draft_revision
  const idempotencyKey = `profile-draft-${expectedRevision}-${stableHash(snapshot)}`
  try {
    const remote = await enterpriseProfileApi.saveDraft(
      context.tenantId,
      context.projectId,
      { draft, expected_draft_revision: expectedRevision, idempotency_key: idempotencyKey },
      { idempotencyKey, expectedRevision, timeoutMs: 30_000 },
    )
    workspace.value = remote
    lastSavedJson = snapshot
    saveState.value = '草稿已保存到云端'
    conflictDraft.value = null
    return true
  } catch (error) {
    if ([413, 422].includes(error?.status)) {
      saveState.value = '内容需要修正，输入已保留'
      errorMessage.value = error?.message || '请检查资料内容后重新保存。'
      return false
    }
    if (error instanceof CloudApiError && error.status === 409) {
      conflictDraft.value = draft
      saveState.value = '发现另一处更新，已读取云端最新版'
      await loadProfile()
      errorMessage.value = '企业资料刚刚在另一页面更新。云端最新版已载入；如需保留刚才填写的内容，请点击“恢复我的未保存内容”。'
      return false
    }
    saveState.value = '网络暂时中断，正在自动重试'
    errorMessage.value = '草稿仍保留在当前页面，网络恢复后会自动继续保存。'
    scheduleRetry()
    return false
  } finally {
    saving.value = false
    if (rerunSave) {
      rerunSave = false
      queueMicrotask(() => saveDraft())
    }
  }
}

function restoreConflictDraft() {
  if (!conflictDraft.value) return
  for (const key of Object.keys(form)) form[key] = String(conflictDraft.value[key] || '')
  pastedSources.value = conflictDraft.value._pasted_sources || {}
  conflictDraft.value = null
  errorMessage.value = ''
  saveDraft(true)
}

async function publishProfile() {
  publishing.value = true
  errorMessage.value = ''
  try {
    profileFields.forEach(field => { touched[field.key] = true })
    if (profileFields.some(field => field.required && !form[field.key].trim())) return
    const checkedInput = JSON.stringify(normalizedDraft())
    if ((await Promise.all(Object.values(textFields).filter(Boolean).map(field => field.validate()))).some(valid => !valid)) return
    if (!await urlChecker.validate(form.competitorUrls) || checkedInput !== JSON.stringify(normalizedDraft())) return
    const saved = await saveDraft()
    if (!saved) return
    if (checkedInput !== JSON.stringify(normalizedDraft())) return
    const currentRevision = workspace.value.current?.revision || 0
    const idempotencyKey = `profile-publish-${currentRevision}-${workspace.value.draft_revision}-${workspace.value.draft_hash}`
    workspace.value = await enterpriseProfileApi.publish(
      context.tenantId,
      context.projectId,
      {
        expected_profile_revision: currentRevision,
        expected_draft_revision: workspace.value.draft_revision,
        idempotency_key: idempotencyKey,
      },
      { idempotencyKey, expectedRevision: currentRevision, timeoutMs: 45_000 },
    )
    saveState.value = `正式版本 v${workspace.value.current.revision} 已保存`
    try { publishProjectChange(projectScope(context.tenantId, context.projectId)) }
    catch (_) { console.warn('正式资料已保存，相关页面可重新读取最新版。') }
  } catch (error) {
    errorMessage.value = error?.message || '正式版本保存失败，资料草稿仍保留在云端。'
    const issues = error?.details?.field_errors
    if (issues?.length) urlFeedback.value = { status: 'invalid', message: issues[0].message, result: { issues } }
  } finally {
    publishing.value = false
  }
}

watch(form, () => {
  if (!ready.value) return
  clearTimeout(saveTimer)
  if (JSON.stringify(normalizedDraft()) === lastSavedJson) {
    if (!saving.value && !publishing.value) saveState.value = savedLabel()
    return
  }
  saveState.value = '有未保存修改'
  saveTimer = setTimeout(() => saveDraft(), 900)
}, { deep: true })

onMounted(async () => {
  if (!tenantId.value || !project.value?.id) {
    await router.replace(authStore.routeForCurrentState())
    return
  }
  try {
    await loadProfile()
  } catch (error) {
    errorMessage.value = error?.message || '暂时无法读取企业资料。'
    saveState.value = '读取失败'
  }
})

watch(() => [ready.value, form.competitorUrls], () => {
  organizePreview.value = null
  if (ready.value) urlChecker.schedule(form.competitorUrls)
}, { flush: 'sync' })

onBeforeUnmount(() => {
  urlChecker.dispose()
  clearTimeout(saveTimer)
  clearTimeout(retryTimer)
})
</script>

<template>
  <section class="profile-page" aria-labelledby="profile-title">
    <header class="profile-header">
      <div>
        <p class="eyebrow">ENTERPRISE PROFILE</p>
        <h1 id="profile-title">企业基本资料</h1>
        <p>资料自动保存到云端。点击保存正式版本后，市场分析会固定读取这一版，不会被另一台电脑静默覆盖。</p>
      </div>
      <router-link class="text-button" to="/workspace/brain">返回项目大脑</router-link>
    </header>

    <div class="profile-context" aria-label="当前企业项目">
      <span>{{ authStore.state.selectedTenant?.name }}</span>
      <strong>{{ project?.name }}</strong>
      <span>{{ saveState }}</span>
    </div>

    <p v-if="errorMessage" class="form-error" role="alert">
      {{ errorMessage }}
      <button v-if="conflictDraft" class="inline-action" type="button" @click="restoreConflictDraft">
        恢复我的未保存内容
      </button>
    </p>

    <section v-if="!ready" class="analysis-empty" aria-live="polite">
      <strong>{{ saveState }}</strong>
      <p>AI 项目经理正在读取当前项目的云端正式资料，不会用空白内容覆盖历史版本。</p>
      <button v-if="saveState === '读取失败'" type="button" @click="loadProfile">重新读取企业资料</button>
    </section>

    <form v-else class="profile-form" @submit.prevent="publishProfile">
      <label v-for="field in profileFields" :key="field.key" :class="{ 'profile-form-wide': field.wide }">
        <span>{{ field.label }} <em v-if="field.required">必填</em></span>
        <PastedTextField v-model="form[field.key]" :ref="el => { textFields[field.key] = el }" :tenant-id="tenantId" :project-id="project?.id"
          :id="field.key" :name="field.key" :placeholder="field.placeholder" rows="4" :required="field.required" :multiline="Boolean(field.multiline)"
          :aria-required="field.required" :aria-invalid="Boolean(fieldError(field))" :aria-describedby="`${field.key}-feedback`"
          @source="updateSource(field.key, $event)" @validation="textStates[field.key] = $event" @blur="touched[field.key] = true" />
        <span :id="`${field.key}-feedback`" aria-live="polite">
          <span v-if="fieldError(field)" class="form-error" role="alert">{{ fieldError(field) }}</span>
          <template v-if="field.key === 'competitorUrls'">
            <span v-if="!fieldError(field)">{{ urlFeedback.message }}</span>
            <span v-for="(issue, index) in urlIssues.slice(1)" :key="index" class="url-issue form-error">{{ issue.message }}</span>
          </template>
        </span>
      </label>
      <section class="profile-form-wide url-tools" aria-label="竞品网址整理">
        <p>必填：至少一个真实竞品官网或公开原文的完整网址，需以 https:// 或 http:// 开头。可粘贴多行资料，再预览整理。</p>
        <button type="button" :disabled="urlFeedback.status === 'checking'" @click="urlChecker.validate(form.competitorUrls)">重新检查网址</button>
        <button type="button" :disabled="!organizedProfile(form, urlFeedback.result)" @click="previewUrls">整理竞品网址</button>
        <button v-if="canUndoUrls" type="button" @click="undoOrganizedUrls">撤销整理</button>
        <section v-if="organizePreview" class="url-preview" aria-label="网址整理预览">
          <h3>整理预览</h3>
          <p>识别到 {{ urlFeedback.result.entries.length }} 个网址、{{ urlFeedback.result.notes.length }} 行说明。原输入保留在上方，确认后说明会追加到“竞品产品/服务”。</p>
          <label>整理后的网址<textarea readonly rows="4" :value="organizePreview.after.competitorUrls" /></label>
          <label>整理后的产品与备注<textarea readonly rows="4" :value="organizePreview.after.competitorProducts" /></label>
          <button type="button" @click="applyOrganizedUrls">应用整理并保留备注</button>
          <button type="button" @click="organizePreview = null">取消整理</button>
        </section>
      </section>
      <section v-if="formalSaveConfirmed" class="profile-save-result profile-form-wide" role="status" aria-live="polite">
        <div>
          <strong>正式资料 v{{ workspace.current.revision }} 已保存</strong>
          <p v-if="workspace.current.created_at">保存时间：{{ new Date(workspace.current.created_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false }) }}</p>
          <p>市场分析将使用这份正式资料。你可以进入下一步。</p>
        </div>
        <button type="button" @click="router.push(`/project-manager/projects/${context.projectId}/company/market`)">进入企业市场分析</button>
      </section>
      <p v-else-if="errorMessage" class="profile-field-error profile-form-wide" role="alert">{{ errorMessage }}</p>
      <p v-else class="profile-form-wide" role="status" aria-live="polite">{{ publishing ? '正在保存正式资料…' : '资料有新修改，请保存正式版本后再分析。' }}</p>
      <footer class="profile-actions profile-form-wide">
        <span>当前正式版本：{{ workspace?.current?.revision ? `v${workspace.current.revision}` : '尚未创建' }}</span>
        <span v-if="urlFeedback.status !== 'valid'">{{ urlFeedback.status === 'checking' ? '网址检查中…' : '请先修正网址，再保存正式版本。' }}</span>
        <button type="submit" :aria-busy="saving || publishing" :disabled="!ready || saving || publishing || !hasUnpublishedChanges || urlFeedback.status !== 'valid'">{{ publishing ? '正在保存正式版本…' : saving ? '正在保存草稿…' : '保存正式版本' }}</button>
      </footer>
    </form>
  </section>
</template>

<style scoped>
.profile-form [aria-invalid="true"], .profile-form [aria-invalid="true"]:focus { border-color: #b42318; box-shadow: 0 0 0 2px #fff0ed; }
.url-issue { display: block; margin-top: 6px; }
.url-tools { min-width: 0; }
.url-tools > button, .url-preview button { margin: 6px 12px 6px 0; padding: 10px 14px; }
.url-tools button { border: 1px solid #92c4c1; border-radius: 10px; background: #eaf7f5; color: #075e5a; font: inherit; font-weight: 600; cursor: pointer; }
.url-tools button:disabled { opacity: .55; cursor: not-allowed; }
.url-tools p { line-height: 1.7; }
.url-preview { padding: 16px; border: 1px solid #87c9c6; border-radius: 12px; }
.url-preview label { display: grid; gap: 8px; margin: 12px 0; }
.url-preview textarea { width: 100%; box-sizing: border-box; }
</style>
